/**
 * Utilitário para ordenar case_actions considerando pré-requisitos e prioridade de tipo
 * 
 * Este arquivo resolve o problema de duplicatas causado pela view cpv_actions_complete
 * ao trabalhar diretamente com case_actions individuais
 */

export interface CaseActionWithRelations {
  id: string; // case_action_id
  action_id: string;
  case_id: string;
  config: any;
  created_at?: string;
  cp_actions: {
    id: string;
    name: string;
    type: string;
    action_type: 'KYC' | 'QUESTIONNAIRE' | 'SIGNATURE' | string;
    config: any;
    questionnaire_id: string | null;
    contract_id: string | null;
  };
}

export interface ActionPrerequisite {
  id: string;
  action_id: string; // case_action_id que tem o pré-requisito
  prereq_id: string; // case_action_id que é o pré-requisito
  description?: string;
}

export interface DoneAction {
  id: string; // done_action_id
  action_id: string; // referencia cp_actions.id
  claim_id: string | null;
  status: string | null;
  // Campos específicos por tipo
  questionnaire_id?: string | null;
  contract_id?: string | null;
  provider?: string | null;
  stage?: string | null; // Para KYC
  passed?: boolean | null; // Para KYC
}

/**
 * Ordena case_actions considerando:
 * 1. Dependências (pré-requisitos) - topological sort
 * 2. Prioridade de tipo quando não há dependências
 * 
 * @param caseActions - Lista de case_actions com suas actions
 * @param prerequisites - Lista de pré-requisitos entre case_actions
 * @returns Array de case_action_ids ordenados
 */
export function sortCaseActions(
  caseActions: CaseActionWithRelations[],
  prerequisites: ActionPrerequisite[]
): string[] {
  console.log('[sortCaseActions] Input:', { 
    caseActionsCount: caseActions.length, 
    prerequisitesCount: prerequisites.length,
    caseActions: caseActions.map(ca => ({ 
      id: ca.id, 
      name: ca.cp_actions.name, 
      type: ca.cp_actions.action_type,
      created_at: ca.created_at 
    })),
    prerequisites: prerequisites.map(pr => ({ 
      action: pr.action_id, 
      prereq: pr.prereq_id,
      description: pr.description
    }))
  });
  
  // 1. Mapear case_action_id para action_type
  const actionTypeMap: Record<string, string> = {};
  caseActions.forEach((ca) => {
    const type = (ca.cp_actions.action_type || ca.cp_actions.type || '').toLowerCase();
    actionTypeMap[ca.id] = type;
  });

  // 2. Mapear dependências: para cada case_action, quais outros são pré-requisito
  const prereqMap: Record<string, Set<string>> = {};
  prerequisites.forEach((pr) => {
    if (!prereqMap[pr.action_id]) prereqMap[pr.action_id] = new Set();
    prereqMap[pr.action_id].add(pr.prereq_id);
  });

  // 3. Função para saber se um case_action tem dependências
  function hasNoPrereq(caseActionId: string): boolean {
    return !prereqMap[caseActionId] || prereqMap[caseActionId].size === 0;
  }

  // 4. Separar case_actions sem dependências e com dependências
  const noPrereq: CaseActionWithRelations[] = [];
  const withPrereq: CaseActionWithRelations[] = [];
  
  caseActions.forEach((ca) => {
    if (hasNoPrereq(ca.id)) {
      noPrereq.push(ca);
    } else {
      withPrereq.push(ca);
    }
  });
  
  console.log('[sortCaseActions] Separated:', {
    noPrereq: noPrereq.map(ca => ({ id: ca.id, name: ca.cp_actions.name })),
    withPrereq: withPrereq.map(ca => ({ 
      id: ca.id, 
      name: ca.cp_actions.name,
      prereqs: Array.from(prereqMap[ca.id] || [])
    }))
  });

  // 5. Ordenar as sem dependências por prioridade de tipo e depois por created_at
  // Ordem: QUESTIONNAIRE -> KYC -> SIGNATURE -> CONTRACT -> outros
  const typePriority = ['questionnaire', 'kyc', 'signature', 'contract'];
  
  noPrereq.sort((a, b) => {
    const typeA = actionTypeMap[a.id] || '';
    const typeB = actionTypeMap[b.id] || '';
    const idxA = typePriority.indexOf(typeA);
    const idxB = typePriority.indexOf(typeB);
    
    // Ordenação primária por tipo
    const typeDiff = (idxA === -1 ? 99 : idxA) - (idxB === -1 ? 99 : idxB);
    if (typeDiff !== 0) return typeDiff;
    
    // Ordenação secundária por created_at (mais antigo primeiro)
    const dateA = new Date(a.created_at || 0).getTime();
    const dateB = new Date(b.created_at || 0).getTime();
    return dateA - dateB;
  });

  // 6. Topological sort para as com dependências
  const result: string[] = noPrereq.map((ca) => ca.id);
  const visited = new Set(result);
  let added = true;
  let iterations = 0;
  const maxIterations = withPrereq.length * 2; // Prevenir loop infinito

  while (withPrereq.length && added && iterations < maxIterations) {
    added = false;
    iterations++;
    
    // Encontrar todas as actions cujas dependências foram satisfeitas
    const readyToAdd: CaseActionWithRelations[] = [];
    
    for (let i = withPrereq.length - 1; i >= 0; i--) {
      const ca = withPrereq[i];
      const prereqsForAction = prereqMap[ca.id] || new Set();
      
      // Verificar se todos os pré-requisitos já estão no resultado
      const allDepsMet = Array.from(prereqsForAction).every((prereqId) => {
        return visited.has(prereqId);
      });

      if (allDepsMet) {
        readyToAdd.push(ca);
        withPrereq.splice(i, 1);
        added = true;
      }
    }
    
    // Ordenar as actions prontas antes de adicionar ao resultado
    readyToAdd.sort((a, b) => {
      const typeA = actionTypeMap[a.id] || '';
      const typeB = actionTypeMap[b.id] || '';
      const idxA = typePriority.indexOf(typeA);
      const idxB = typePriority.indexOf(typeB);
      
      const typeDiff = (idxA === -1 ? 99 : idxA) - (idxB === -1 ? 99 : idxB);
      if (typeDiff !== 0) return typeDiff;
      
      const dateA = new Date(a.created_at || 0).getTime();
      const dateB = new Date(b.created_at || 0).getTime();
      return dateA - dateB;
    });
    
    // Adicionar ao resultado
    readyToAdd.forEach((ca) => {
      result.push(ca.id);
      visited.add(ca.id);
    });
  }

  // 7. Se sobrou algum (ciclo de dependências), adiciona no final
  if (withPrereq.length > 0) {
    console.warn('[sortCaseActions] Circular dependencies detected:', 
      withPrereq.map(ca => ({ 
        id: ca.id, 
        name: ca.cp_actions.name,
        prereqs: Array.from(prereqMap[ca.id] || [])
      }))
    );
  }
  
  withPrereq.forEach((ca) => result.push(ca.id));

  console.log('🔎 [WIZARD_DEBUG] TOPOLOGICAL_ORDER_RESULT', {
    totalActions: result.length,
    orderedIds: result,
    orderedSummary: result.map((id, idx) => {
      const ca = caseActions.find(c => c.id === id);
      return `[${idx}] ${ca?.cp_actions.name || '?'} (${ca?.cp_actions.action_type || '?'}) id=${id.slice(0,8)} prereqs=${Array.from(prereqMap[id] || []).map(p => p.slice(0,8)).join(',') || 'none'}`;
    }),
  });
  return result;
}

/**
 * Encontra a done_action correspondente para um case_action
 * 
 * @param caseAction - Case action para buscar
 * @param doneActions - Lista de done actions do claim
 * @returns Done action correspondente ou null
 */
export function findDoneActionForCaseAction(
  caseAction: CaseActionWithRelations,
  doneActions: DoneAction[]
): DoneAction | null {
  // Buscar done_action que corresponda ao action_id
  const action = caseAction.cp_actions;
  
  return doneActions.find((done) => {
    // Verificar se o done_action referencia o mesmo action_id
    if (done.action_id !== action.id) return false;

    // Verificações adicionais por tipo
    if (action.questionnaire_id && done.questionnaire_id) {
      return done.questionnaire_id === action.questionnaire_id;
    }
    
    if (action.contract_id && done.contract_id) {
      return done.contract_id === action.contract_id;
    }

    // Para KYC, verificar provider
    if (action.action_type === 'KYC' && action.config?.provider) {
      return done.provider === action.config.provider;
    }

    return true;
  }) || null;
}
